// src/components/pages/Home.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  FaSearch, FaArrowRight, FaTruck, FaTag, FaLeaf,
  FaHeart, FaGift, FaRegCalendarAlt, FaUtensilSpoon, FaTrain, FaBox,
  FaUtensils, FaShoppingCart, FaGooglePlay, FaApple, FaQrcode,
  FaMapMarkerAlt, FaStar, FaShieldAlt, FaAndroid
} from 'react-icons/fa';
import { fetchProductsAsync } from '../../redux/slices/productSlice';
import { getColors } from '../../styles/colors';
import LoginModal from '../common/LoginModal';
import { setShowLoginModal } from '../../redux/slices/authSlice';

// ============================================================
// ✅ APP DOWNLOAD URL
// ============================================================
const APP_DOWNLOAD_URL = 'https://play.google.com/store/apps/details?id=com.siancetiffin.app';

// ============================================================
// DATA ARRAYS
// ============================================================

const heroImages = [
  {
    id: 1,
    url: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1920&h=1080&fit=crop',
    alt: 'Delicious Food Platter'
  },
  {
    id: 2,
    url: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=1920&h=1080&fit=crop',
    alt: 'Pizza'
  },
  {
    id: 3,
    url: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=1920&h=1080&fit=crop',
    alt: 'Burger'
  },
  {
    id: 4,
    url: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1920&h=1080&fit=crop',
    alt: 'Biryani'
  },
  {
    id: 5,
    url: 'https://images.unsplash.com/photo-1547592166-23ac45744acd?w=1920&h=1080&fit=crop',
    alt: 'Indian Thali'
  }
];

const appFeatures = [
  { icon: FaLeaf, title: 'Veg Mode', color: '#4CAF50' },
  { icon: FaHeart, title: 'Healthy', color: '#2196F3' },
  { icon: FaGift, title: 'Plan a Party', color: '#FF5722' },
  { icon: FaGift, title: 'Gift Cards', color: '#9C27B0' },
  { icon: FaRegCalendarAlt, title: 'Schedule Order', color: '#FF9800' },
  { icon: FaTag, title: 'Offers', color: '#E91E63' },
  { icon: FaUtensilSpoon, title: 'Gourmet', color: '#795548' },
  { icon: FaTrain, title: 'Food on Train', color: '#3F51B5' },
  { icon: FaBox, title: 'Collections', color: '#009688' },
];

const goldBenefits = [
  { title: 'Free Delivery', description: 'At all restaurants within 7 km', icon: FaTruck },
  { title: 'Up to 30% extra off', description: 'At 20,000+ partner restaurants', icon: FaTag },
];

const stats = [
  { number: '3,00,000+', label: 'restaurants' },
  { number: '800+', label: 'cities' },
  { number: '3 billion+', label: 'orders delivered' },
];

const whyChooseUs = [
  { icon: FaTruck, title: 'Fast Delivery', description: 'Hot & fresh within 30 min', color: '#FD8A36' },
  { icon: FaShieldAlt, title: '100% Hygiene', description: 'Highest safety standards', color: '#2E7D32' },
  { icon: FaLeaf, title: 'Fresh Ingredients', description: 'Sourced from local farms', color: '#2196F3' },
  { icon: FaStar, title: 'Quality Guarantee', description: 'Satisfaction guaranteed', color: '#FF5722' },
];

// ============================================================
// COMPONENT
// ============================================================

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading } = useSelector((state) => state.products);
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  // ✅ Get auth state
  const { isAuthenticated, showLoginModal, user } = useSelector((state) => state.auth);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // ✅ Check authentication - ONLY for redirection if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const isNew = localStorage.getItem('isNewUser') === 'true';
    const profileComplete = localStorage.getItem('profileCompleted') === 'true';
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const hasName = storedUser?.name && storedUser.name !== 'User' && storedUser.name !== 'Account';
    
    // ✅ If user is new or profile not complete, redirect to profile form
    if (token && (isNew || !profileComplete || !hasName)) {
      console.log('🔄 New user or incomplete profile → Redirecting to profile form');
      navigate('/profile-form');
      return;
    }
    
    // ✅ If token exists and user has profile, hide login modal
    if (token && profileComplete && hasName) {
      dispatch(setShowLoginModal(false));
      return;
    }
    
    // ✅ If no token, DO NOT show login modal on homepage
    // Homepage is public - anyone can view it
    if (!token) {
      dispatch(setShowLoginModal(false));
    }
  }, [dispatch, isAuthenticated, navigate, user]);

  // ✅ Fetch products on component mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        await dispatch(fetchProductsAsync()).unwrap();
      } catch (err) {
        console.error('❌ Failed to load products:', err);
      } finally {
        setHasLoaded(true);
      }
    };
    loadProducts();
  }, [dispatch]);

  // Hero image slider
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ✅ Handle service click - show login modal if not authenticated
  const handleServiceClick = (e, path) => {
    const token = localStorage.getItem('token');
    if (!token) {
      e.preventDefault();
      dispatch(setShowLoginModal(true));
      // Store the intended path to redirect after login
      localStorage.setItem('redirectAfterLogin', path);
      return;
    }
    // If authenticated, navigate normally via Link
  };

  // ============================================================
  // ✅ HERO SECTION - Full Screen
  // ============================================================
  const HeroSection = () => (
    <div className="relative w-full" style={{ 
      minHeight: '100vh', 
      height: '100vh', 
      maxHeight: '100vh', 
      width: '100vw', 
      margin: 0, 
      padding: 0, 
      borderRadius: 0, 
      position: 'relative', 
      left: '50%', 
      right: '50%', 
      marginLeft: '-50vw', 
      marginRight: '-50vw', 
      marginTop: '-80px', 
      top: 0, 
      zIndex: 0 
    }}>
      <div className="absolute inset-0 w-full h-full">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000" 
          style={{ 
            backgroundImage: `url(${heroImages[currentImageIndex].url})`, 
            width: '100%', 
            height: '100%', 
            backgroundSize: 'cover', 
            backgroundPosition: 'center', 
            backgroundRepeat: 'no-repeat'
          }} 
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80"></div>
      <div className="relative z-10 flex flex-col items-center justify-center min-h-[100vh] px-4 sm:px-6 lg:px-8 text-center text-white w-full pt-20">
        <div className="max-w-4xl mx-auto">
          {/* ✅ Yellow badge - keeping original color */}
          <div className="inline-block bg-gradient-to-r from-yellow-400 to-yellow-500 px-6 py-2 rounded-full text-xs font-bold text-gray-900 mb-6 shadow-lg">
            🎉 Get 20% Off Your First Order!
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
            ❤️ Dil Se Plate Tak
          </h1>
          
          <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-yellow-300 font-semibold mt-1">
            🍛 Garma Garam
          </p>
          
          <p className="text-base sm:text-lg text-white/90 max-w-2xl mx-auto mt-2 mb-5">
            Ghar jaisa swaad, har dabbe ke saath
          </p>
          
          <div className="max-w-2xl mx-auto w-full px-4 sm:px-0">
            <div className="relative bg-white rounded-full shadow-2xl overflow-hidden">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input 
                type="text" 
                placeholder="Search for Thali, dishes, restaurants..." 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
                className="w-full py-3.5 sm:py-4 pl-14 pr-4 text-gray-700 placeholder-gray-400 focus:outline-none text-sm sm:text-base" 
                style={{ outline: 'none' }} 
              />
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mt-3 text-white/80 text-xs sm:text-sm">
            <FaMapMarkerAlt className="text-yellow-300" /> 
            <span>Kalyan City, West Bengal, 712502</span>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-6">
            {/* ✅ Tiffin Service - Orange */}
            <Link 
              to="/tiffin-service" 
              onClick={(e) => handleServiceClick(e, '/tiffin-service')}
            >
              <button 
                className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <FaUtensils className="text-2xl" />
                <div className="text-left">
                  <p className="text-[10px] text-white/80">EXPLORE</p>
                  <p className="text-lg font-bold">Tiffin Service</p>
                </div>
                <FaArrowRight className="ml-2 text-white/70" />
              </button>
            </Link>
            
            {/* ✅ Cart Service - EXACT SAME YELLOW as badge */}
            <Link 
              to="/cart-service" 
              onClick={(e) => handleServiceClick(e, '/cart-service')}
            >
              <button 
                className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <FaShoppingCart className="text-2xl" style={{ color: '#111827' }} />
                <div className="text-left">
                  <p className="text-[10px] text-gray-700/80">EXPLORE</p>
                  <p className="text-lg font-bold" style={{ color: '#111827' }}>Cart Service</p>
                </div>
                <FaArrowRight className="ml-2" style={{ color: '#111827' }} />
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // ✅ BETTER FOOD SECTION - Full Width
  // ============================================================
  const BetterFoodSection = () => (
    <div className="py-20 px-4 w-full" style={{ backgroundColor: colors.background }}>
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: colors.text }}>
              Better food for <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600">more people</span>
            </h2>
            <p className="text-lg" style={{ color: colors.textSecondary }}>
              For over a decade, we've enabled our customers to discover new tastes, delivered right to their doorstep
            </p>
            <div className="grid grid-cols-3 gap-4 mt-8">
              {stats.map((stat, index) => (
                <div 
                  key={index}
                  className="text-center p-4 rounded-2xl border-2"
                  style={{ 
                    backgroundColor: colors.card,
                    borderColor: colors.primary + '30',
                  }}
                >
                  <p className="text-2xl sm:text-3xl font-bold" style={{ color: colors.primary }}>
                    {stat.number}
                  </p>
                  <p className="text-xs sm:text-sm" style={{ color: colors.textSecondary }}>
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img 
              src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&h=500&fit=crop" 
              alt="Better food for more people"
              className="rounded-2xl shadow-2xl w-full h-72 sm:h-80 object-cover"
            />
            <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-3">
              <span className="text-3xl">🏆</span>
              <div>
                <p className="text-xs font-semibold opacity-80">10+ Years</p>
                <p className="text-sm font-bold">of excellence</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // ✅ WHY CHOOSE US - Full Width
  // ============================================================
  const WhyChooseUs = () => (
    <div className="py-20 px-4 w-full" style={{ backgroundColor: isDark ? '#1a1a2e' : '#f8f9fa' }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4" style={{ color: colors.text }}>
            Why Choose Us?
          </h2>
          <p className="text-lg" style={{ color: colors.textSecondary }}>
            We deliver quality and taste with every meal
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {whyChooseUs.map((item, index) => (
            <div
              key={index}
              className="text-center p-6 rounded-2xl hover:shadow-xl transition-all duration-300"
              style={{ 
                backgroundColor: colors.card,
                border: `1px solid ${colors.border}`,
              }}
            >
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: item.color + '20', color: item.color }}
              >
                <item.icon className="text-3xl" />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: colors.text }}>
                {item.title}
              </h3>
              <p className="text-sm" style={{ color: colors.textSecondary }}>
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ============================================================
  // ✅ APP FEATURES SECTION - Full Width
  // ============================================================
  const AppFeaturesSection = () => (
    <div className="py-20 px-4 w-full" style={{ backgroundColor: colors.background }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4" style={{ color: colors.text }}>
            What's waiting for you on the app?
          </h2>
          <p className="text-lg" style={{ color: colors.textSecondary }}>
            Our app is packed with features that enable you to experience food delivery like never before
          </p>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-4">
          {appFeatures.map((feature, index) => (
            <div
              key={index}
              className="flex flex-col items-center p-4 rounded-2xl transition-all duration-300 cursor-pointer hover:shadow-lg"
              style={{ 
                backgroundColor: colors.card,
                border: `1px solid ${colors.border}`,
              }}
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl mb-2"
                style={{ backgroundColor: feature.color }}
              >
                <feature.icon />
              </div>
              <span className="text-xs text-center font-medium" style={{ color: colors.text }}>
                {feature.title}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ============================================================
  // ✅ GOLD SECTION - Full Width
  // ============================================================
  const GoldSection = () => (
    <div className="py-20 px-4 w-full" style={{ backgroundColor: isDark ? '#1a1a2e' : '#f8f9fa' }}>
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block bg-gradient-to-r from-yellow-400 to-yellow-500 px-4 py-1 rounded-full text-xs font-bold text-gray-900 mb-4 shadow-lg">
              👑 GOLD
            </div>
            <h2 className="text-4xl font-bold mb-4" style={{ color: colors.text }}>
              India's Top Savings <br />Program for Food Lovers
            </h2>
            <div className="space-y-4 mt-6">
              {goldBenefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-3 rounded-xl"
                  style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,193,7,0.1)' }}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FFD70020', color: '#FFD700' }}>
                    <benefit.icon className="text-lg" />
                  </div>
                  <div>
                    <p className="font-semibold" style={{ color: colors.text }}>{benefit.title}</p>
                    <p className="text-sm" style={{ color: colors.textSecondary }}>{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <button 
              className="mt-8 px-8 py-3 rounded-full font-semibold text-white shadow-lg hover:shadow-xl transition-all"
              style={{ backgroundColor: '#FD8A36' }}
            >
              Learn More
            </button>
          </div>
          <div className="relative">
            <div className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-orange-500 rounded-2xl p-8 text-white text-center shadow-2xl">
              <div className="text-6xl mb-4">👑</div>
              <h3 className="text-3xl font-bold">GOLD</h3>
              <p className="mt-2 opacity-90">India's Top Savings Program</p>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                  <p className="text-2xl font-bold">✓</p>
                  <p className="text-sm">Free Delivery</p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                  <p className="text-2xl font-bold">✓</p>
                  <p className="text-sm">30% Extra Off</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // ✅ DOWNLOAD APP SECTION - App Store and "Available on Android" REMOVED
  // ============================================================
  const DownloadAppSection = () => (
    <div className="py-20 px-4 w-full" style={{ backgroundColor: colors.background }}>
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-1 rounded-full text-xs font-bold text-white mb-4 shadow-lg">
              📲 DOWNLOAD
            </div>
            <h2 className="text-4xl font-bold mb-4" style={{ color: colors.text }}>
              Download the app now!
            </h2>
            <p className="text-lg" style={{ color: colors.textSecondary }}>
              Experience seamless online ordering only on the Foodies app
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-6">
              <a
                href={APP_DOWNLOAD_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-6 py-3 rounded-lg hover:shadow-xl transition-all duration-300"
                style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
              >
                <FaGooglePlay className="text-2xl" style={{ color: colors.primary }} />
                <div className="text-left">
                  <p className="text-[10px]" style={{ color: colors.textSecondary }}>GET IT ON</p>
                  <p className="text-sm font-semibold" style={{ color: colors.text }}>Google Play</p>
                </div>
              </a>
              {/* ✅ App Store button REMOVED */}
            </div>
            {/* ✅ "Available on Android" text REMOVED */}
          </div>
          <div className="flex justify-center">
            <a
              href={APP_DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl p-8 text-white text-center shadow-2xl hover:scale-105 transition-transform duration-300 cursor-pointer"
            >
              <FaQrcode className="text-6xl mx-auto mb-4" />
              <p className="font-semibold">Scan the QR code</p>
              <p className="text-sm opacity-80">to download the app</p>
              <div className="mt-3 flex items-center justify-center gap-2 text-xs bg-white/20 rounded-lg px-3 py-1.5">
                <FaGooglePlay />
                <span>Available on Play Store</span>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // ✅ LOADING STATE
  // ============================================================

  if (isLoading && !hasLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 mx-auto" style={{ borderColor: colors.primary }}></div>
          <p className="mt-4" style={{ color: colors.text }}>Loading delicious meals...</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // ✅ MAIN RENDER
  // ============================================================

  const token = localStorage.getItem('token');
  const isNew = localStorage.getItem('isNewUser') === 'true';
  const profileComplete = localStorage.getItem('profileCompleted') === 'true';
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const hasName = storedUser?.name && storedUser.name !== 'User' && storedUser.name !== 'Account';

  // ✅ If user is new or profile not complete, show nothing (will redirect)
  if (token && (isNew || !profileComplete || !hasName)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mx-auto" style={{ borderColor: colors.primary }}></div>
          <p className="mt-4" style={{ color: colors.text }}>Redirecting to profile setup...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        body::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }
        body {
          -ms-overflow-style: none;
          scrollbar-width: none;
          overflow-x: hidden;
        }
      `}</style>

      <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
        {/* ✅ Main Content - Always visible on homepage */}
        <div>
          <HeroSection />
          <BetterFoodSection />
          <WhyChooseUs />
          <AppFeaturesSection />
          <GoldSection />
          <DownloadAppSection />

          {/* ✅ SCROLL TO TOP BUTTON */}
          {showScrollTop && (
            <button
              onClick={scrollToTop}
              className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full shadow-lg text-white flex items-center justify-center"
              style={{ backgroundColor: colors.primary }}
            >
              ↑
            </button>
          )}
        </div>

        {/* ✅ LOGIN MODAL - Only show when user clicks on service and is not authenticated */}
        <LoginModal 
          isOpen={showLoginModal} 
          onClose={() => {
            dispatch(setShowLoginModal(false));
          }} 
        />
      </div>
    </>
  );
};

export default Home;